'use client';

import React, { useState, useCallback } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useRouter, useSearchParams } from 'next/navigation';

import Toast from '@atoms/Toast';
import Button from '@atoms/new/button';
import { getAuth } from '@services/identity.service';
import { logsToAwsCloudWatch } from 'services/logger.service';

function ErrorFallback({ resetErrorBoundary, retryCount, setRetryCount, searchParams }) {
  const handleRetry = () => {
    if (retryCount === 1) {
      resetErrorBoundary();
    } else if (retryCount > 1 && retryCount < 4) {
      const params = new URLSearchParams(searchParams.toString());
      params.set('retry', retryCount);
      window.location.search = params.toString();
    }
  };

  return (
    <div className="fixed bottom-8 left-8 z-9999 animate-slideLeft">
      <Toast
        variant="alert"
        width="w-[400px]"
        showIcon
        header="Something went wrong on our side"
        headerClassName="font-semibold"
        onClose={resetErrorBoundary}
        body={
          <div className="flex gap-4 flex-col">
            <p>
              {retryCount === 1
                ? 'We couldn\'t load everything — try again to fix it.'
                : retryCount >= 3
                  ? 'Multiple attempts failed — let\'s start fresh.'
                  : 'Looks like it\'s still stuck — try refreshing.'}
            </p>
            <Button
              onClick={handleRetry}
              kind="tertiary"
              aria-label="Retry or refresh"
              className="max-w-fit dark:border-white dark:text-white dark:hover:bg-white dark:hover:text-grey-900"
            >
              {retryCount === 1 ? 'Try again' : retryCount >= 3 ? 'Start fresh' : 'Refresh page'}
            </Button>
          </div>
        }
        position="bottom-left"
      />
    </div>
  );
}

export default function ErrorBoundaryWrapper({ children }) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [retryCount, setRetryCount] = useState(parseInt(searchParams.get('retry') || '0', 10));

  const handleError = (error, errorInfo) => {
    setRetryCount((prev) => prev + 1);
    const auth = getAuth();
    logsToAwsCloudWatch({
      logMessage: 'BrowserError(error boundary wrapper): Error occurred in the component',
      logData: {
        retryCount,
        message: error.message,
        userId: auth?.id || '',
        errorStack: error.stack,
        componentStack: errorInfo.componentStack,
      },
      level: 'ERROR',
    });
  };

  const fallbackRenderer = useCallback(({ resetErrorBoundary }) => (
    <ErrorFallback
      resetErrorBoundary={resetErrorBoundary}
      retryCount={retryCount}
      setRetryCount={setRetryCount}
      router={router}
      searchParams={searchParams}
    />
  ), [retryCount]);

  return (
    <ErrorBoundary
      fallbackRender={fallbackRenderer}
      onError={handleError}
    >
      {children}
    </ErrorBoundary>
  );
}
